import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// Types
import { AuthContextType } from '../../../../../hooks/useAuth';

// GraphQL
import { client } from '../../../../../gql/client';
import { fetchMaterialsCatalog } from '../../../../../gql/services/materialsCatalog';
import { selectMaterialsValue } from '../../../../../gql/selectors/actions';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';

// Utils
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import {
  FETCH_MEASURE_TYPES,
  GET_MEASURE_TYPES_BY_ID,
} from '../../../../../gql/queries/actions/measureTypes';
import {
  FETCH_CODIFICATIONS,
  GET_CODIFICATION_BY_ID,
} from '../../../../../gql/queries/actions/codification';
import {
  FETCH_SUBCODIFICATIONS,
  GET_SUBCODIFICATION_BY_ID,
} from '../../../../../gql/queries/actions/subCodification';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { FETCH_TEAMS } from '../../../../../gql/queries/users';

// Utils — materiales por equipo (2026-07-31)
import { buildWizardMaterialRow } from '../../../../../utils/actionMaterials';
import {
  scopeMaterialsForWizard,
  collectSelectedMaterialIds,
  getCurrentUserTeamIds,
} from '../../../../../utils/materialTeamScope';
import { isBasicAction } from '../../../../../utils/order';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

const loadFormValues = async (
  userData: AuthContextType | null,
  actionId: string
) => {
  try {
    if (userData?.user) {
      const formValues = await selectMaterialsValue(actionId);
      return formValues;
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log(`> Erro loading materialsData`, err);
  }
};

export const MaterialsValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
    // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
    // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
    // cualquier `await` (diagnóstico 2026-10-08 § 8).
    reconcileValidationActionContext(params.id);
    // La lista de fotos y el croquis en curso también son de la actuación
    // de la URL. Las carpetas de OPFS no se tocan: las comparten las
    // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
    claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

    // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
    // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
    // el router termine la navegación al instante y el `<Fallback>` enseñe el
    // Loader DENTRO de la pantalla nueva.
    const load = async () => {
      const stringifyForm = sessionStorage.getItem(
        formActionValidationNaming.NEW_VALIDATION_MATERIALS_FORM
      );

      const stringifyActionData = sessionStorage.getItem(
        formActionNaming.NEW_VALIDATION_ACTION_DATA
      );

      let sessionStorageData;
      let actionData;
      let parentExpeditionOrderStatus;
      let state;
      // OT padre completa: su `type` decide si esta actuación es una BÁSICA (padre
      // de tipo surveillanceRoute) y su `teamIDs` es el equipo contra el que se
      // filtran los materiales. Ambas ramas de abajo ya la consultan.
      let parentOrderData: { type?: string; teamIDs?: string[] } | undefined;
      // `containerType` de la Order de la ACTUACIÓN: vía barata del discriminador
      // canónico, ya viene en el fragment y no cuesta una query.
      let actionOrderContainerType: string | null | undefined;

      if (stringifyForm) {
        sessionStorageData = JSON.parse(
          decompressSessionStorage(stringifyForm)
        );
      }

      if (stringifyActionData) {
        actionData = JSON.parse(decompressSessionStorage(stringifyActionData));
        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: actionData.orderId,
          },
        });
        const parentOrder = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: order.parentExpeditionOrderIDs[0],
          },
        });
        parentExpeditionOrderStatus = parentOrder.data.order.state;
        parentOrderData = parentOrder.data.order;
        actionOrderContainerType = order.containerType;
        state = order.state;
      } else if (params.id) {
        // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have actionData
        const {
          data: { action },
        } = await client.query({
          query: GET_ACTION_BY_ID,
          variables: { id: params.id },
        });

        const {
          data: { order },
        } = await client.query({
          query: GET_ORDER_BY_ID,
          variables: { id: action.orderIDs[0] },
        });

        actionData = {
          id: params.id,
          orderId: order.id,
          roid: order.roid,
          isActionEdited: 'false',
        };

        const parentOrder = await client.query({
          query: GET_ORDER_BY_ID,
          variables: {
            id: order.parentExpeditionOrderIDs[0],
          },
        });
        parentExpeditionOrderStatus = parentOrder.data.order.state;
        parentOrderData = parentOrder.data.order;
        actionOrderContainerType = order.containerType;
        state = order.state;
      }

      // Fresco si hay red, de la caché si no (ver `fetchMaterialsCatalog`): la
      // asignación de equipos se decide en Gestión desde otro dispositivo.
      const materialsUnsorted = await fetchMaterialsCatalog();

      await Promise.all([
        client.query({
          query: FETCH_MEASURE_TYPES,
        }),
        client.query({
          query: FETCH_CODIFICATIONS,
        }),
        client.query({
          query: FETCH_SUBCODIFICATIONS,
        }),
      ]);

      const materialsData = await Promise.all(
        materialsUnsorted.map(
          async (material: {
            id: string;
            materialId: string;
            name: string;
            typeOfMeasurement: string;
            codification: string;
            subCodification: string;
            measureTypeIDs: string[];
            codificationIDs: string[];
            subCodificationIDs: string[];
            teamIDs?: string[];
            isActive?: boolean | null;
          }) => {
            const [
              {
                data: { measureType },
              },
              {
                data: { codification },
              },
              {
                data: { subCodification },
              },
            ] = await Promise.all([
              client.query({
                query: GET_MEASURE_TYPES_BY_ID,
                variables: {
                  id: material?.measureTypeIDs[0],
                },
                fetchPolicy: 'cache-first',
              }),
              client.query({
                query: GET_CODIFICATION_BY_ID,
                variables: {
                  id: material?.codificationIDs[0],
                },
                fetchPolicy: 'cache-first',
              }),
              client.query({
                query: GET_SUBCODIFICATION_BY_ID,
                variables: {
                  id: material?.subCodificationIDs[0],
                },
                fetchPolicy: 'cache-first',
              }),
            ]);

            return buildWizardMaterialRow(material, {
              typeOfMeasurement: measureType.name,
              codification: codification.name,
              subCodification: subCodification.name,
            });
          }
        )
      );

      const sortedMaterials = [...materialsData].sort((a, b) =>
        a.materialId.localeCompare(b.materialId)
      );
      const loaderToResolve = await loadFormValues(userData, actionData.id);

      // Materiales por equipo: mismo filtro que al crear, para que la actuación se
      // vea igual en ambos caminos. Se aplica en TODA actuación, básica o normal —
      // las normales las rellena también la gente de tablet, que pertenece a un
      // equipo. Los materiales YA guardados nunca se ocultan (`keepIds`), o si no
      // desaparecerían de la pantalla filas con datos reales.
      const materials = await (async () => {
        try {
          const {
            data: { teams },
          } = await client.query({
            query: FETCH_TEAMS,
            fetchPolicy: 'cache-first',
          });
          return scopeMaterialsForWizard({
            materials: sortedMaterials,
            tenantTeamIds: (teams ?? []).map((team: { id: string }) => team.id),
            // Solo en la básica: el equipo del RV. En una actuación normal este
            // criterio no aplica (ver el loader de creación).
            orderTeamIds: isBasicAction({
              containerType: actionOrderContainerType,
              parentOrderType: parentOrderData?.type,
            })
              ? (parentOrderData?.teamIDs ?? [])
              : [],
            userTeamIds: getCurrentUserTeamIds(),
            keepIds: collectSelectedMaterialIds(
              loaderToResolve?.materialsTable
            ),
          });
        } catch (err) {
          // Fail-open: mejor de más que dejar a gestión sin catálogo.
          console.log('[materials] scope por equipo omitido:', err);
          return sortedMaterials;
        }
      })();
      const combinedData = {
        ...loaderToResolve,
        ...sessionStorageData,
        ...actionData,
        parentExpeditionOrderStatus,
        state,
        // Actuació bàsica: desde 2026-08-11 esta pestaña es su PASO TERMINAL (la
        // de fauna se retiró), así que necesita el discriminador para pintar el
        // pie de Guardar/Validar y para hacer el submit. Por DATO, nunca por la
        // URL: la validación vive bajo /work-orders/. Cero consultas nuevas — la
        // Order del padre ya se carga arriba para el filtro de materiales.
        isBasicAction: isBasicAction({
          containerType: actionOrderContainerType,
          parentOrderType: parentOrderData?.type,
        }),
        dataFromServer: { ...loaderToResolve, materials },
        materialsData: materials.map((material: any) => ({
          ...material,
          materialUnitId: null,
        })),
      };

      return combinedData;
    };

    return { data: load() };
  };
